import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  ChevronDown,
  CircleAlert,
  Gauge,
  Info,
  Megaphone,
  LoaderCircle,
  Minus,
  Plus,
  ShieldCheck,
  Ticket,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { formatUsd } from "../utils/money";
import { catalogMinerToProduct, marketplaceApi } from "../api/marketplace";
import { ProductCard } from "../components/ProductCard";
import { useCopy } from "../i18n/copy";
import { useSession } from "../store/session";
import { engagementApi } from "../api/engagement";
import { htmlToText } from "../utils/sanitizeHtml";
import { RemoteNoticeDialog } from "../components/RemoteNoticeDialog";
import { Countdown } from "../components/Countdown";

export function MarketPage() {
  const { isPreview } = useSession();
  const { tr } = useCopy();
  const [type, setType] = useState<1 | 2>(1);
  const [sort, setSort] = useState<"default" | "return" | "price">("default");
  const query = useQuery({
    queryKey: ["miner-catalog", type],
    queryFn: () => marketplaceApi.catalog(type),
    enabled: !isPreview,
  });
  const active = useQuery({
    queryKey: ["active-counts"],
    queryFn: marketplaceApi.activeCounts,
    enabled: !isPreview,
  });
  const notice = useQuery({
    queryKey: ["notice", 4],
    queryFn: () => engagementApi.notice(4),
    enabled: !isPreview,
  });
  const activity = [
    { label: "Nivel 1 activo", value: active.data?.one },
    { label: "Nivel 2 activo", value: active.data?.two },
    { label: "Nivel 3 activo", value: active.data?.three },
  ];
  const products = useMemo(() => {
    const values = (query.data || []).map((item) =>
      catalogMinerToProduct(item, type),
    );
    if (sort === "return")
      return values.sort((a, b) => b.dailyIncome - a.dailyIncome);
    if (sort === "price") return values.sort((a, b) => a.price - b.price);
    return values;
  }, [query.data, sort, type]);
  return (
    <main className="page">
      <header className="page-title-row">
        <div>
          <p className="eyebrow">{tr("Catálogo activo")}</p>
          <h1>{tr("Mercado de equipos")}</h1>
        </div>
      </header>
      <section className="market-activity">
        <div className="market-activity-stats">
          {activity.map((item) => (
            <span key={item.label}>
              <small>{tr(item.label)}</small>
              <strong>{active.isLoading ? "—" : (item.value ?? 0)}</strong>
            </span>
          ))}
        </div>
        {notice.data?.content && (
          <div className="market-notice">
            <Megaphone />
            <span>{htmlToText(notice.data.content)}</span>
          </div>
        )}
      </section>
      <div className="catalog-controls">
        <div className="segmented">
          <button
            className={type === 1 ? "active" : ""}
            onClick={() => setType(1)}
          >
            {tr("Computación en la nube")}
          </button>
          <button
            className={type === 2 ? "active" : ""}
            onClick={() => setType(2)}
          >
            {tr("Minería")}
          </button>
        </div>
        <label>
          <span>{tr("Ordenar")}</span>
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value as typeof sort)}
          >
            <option value="default">{tr("Recomendados")}</option>
            <option value="return">{tr("Mayor rendimiento")}</option>
            <option value="price">{tr("Menor inversión")}</option>
          </select>
          <ChevronDown />
        </label>
      </div>
      {query.isLoading && (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando equipos…")}
        </div>
      )}
      {query.isError && (
        <div className="form-feedback error">
          <CircleAlert /> {tr("No pudimos cargar los equipos.")}
        </div>
      )}
      {isPreview ? (
        <div className="empty-state">
          <Gauge />
          <h2>{tr("Inicia sesión para consultar los equipos disponibles")}</h2>
          <Link className="button primary" to="/auth/login">
            {tr("Iniciar sesión")} <ArrowRight />
          </Link>
        </div>
      ) : (
        <>
          <div className="product-grid market-products">
            {products.map((product) => (
              <ProductCard product={product} key={product.id} />
            ))}
          </div>
          {!query.isLoading && !products.length && (
            <div className="empty-state">
              <Gauge />
              <h2>{tr("No hay equipos disponibles")}</h2>
            </div>
          )}
        </>
      )}
    </main>
  );
}

export function ProductDetailPage() {
  const { productId } = useParams();
  const { user, isPreview } = useSession();
  const { tr } = useCopy();
  const client = useQueryClient();
  const [quantity, setQuantity] = useState(1);
  const [couponId, setCouponId] = useState<string | number>();
  const [reviewing, setReviewing] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const catalog = useQuery({
    queryKey: ["miner-detail-catalog"],
    queryFn: async () => {
      const [cloud, miners] = await Promise.all([
        marketplaceApi.catalog(1),
        marketplaceApi.catalog(2),
      ]);
      return [
        ...cloud.map((item) => ({ ...item, type: 1 })),
        ...miners.map((item) => ({ ...item, type: 2 })),
      ];
    },
    enabled: !isPreview,
  });
  const coupons = useQuery({
    queryKey: ["purchase-coupons"],
    queryFn: () => marketplaceApi.coupons(),
    enabled: !isPreview,
  });
  const raw = catalog.data?.find(
    (item) => Number(item.id) === Number(productId),
  );
  const product = raw
    ? catalogMinerToProduct(raw, raw.type === 2 ? 2 : 1)
    : undefined;
  const selectedCoupon = coupons.data?.find((item) => item.id === couponId);
  const discount = Number(selectedCoupon?.coupon_quota || 0);
  const total = Math.max(Number(product?.price || 0) * quantity - discount, 0);
  const max = Math.max(1, Number(raw?.max_mall || 1));
  const purchase = useMutation({
    mutationFn: () =>
      marketplaceApi.buy(Number(product?.id), quantity, couponId),
    onSuccess: () => {
      setReviewing(false);
      client.invalidateQueries({ queryKey: ["session-user"] });
      client.invalidateQueries({ queryKey: ["user-miners"] });
    },
  });
  if (isPreview)
    return (
      <main className="page">
        <Link className="back-link" to="/market">
          <ArrowLeft /> {tr("Volver al mercado")}
        </Link>
        <div className="empty-state">
          <Gauge />
          <h1>{tr("Inicia sesión para consultar los equipos disponibles")}</h1>
          <Link className="button primary" to="/auth/login">
            {tr("Iniciar sesión")} <ArrowRight />
          </Link>
        </div>
      </main>
    );
  if (catalog.isLoading)
    return (
      <main className="page">
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando equipo…")}
        </div>
      </main>
    );
  if (!product)
    return (
      <main className="page">
        <Link className="back-link" to="/market">
          <ArrowLeft /> {tr("Volver al mercado")}
        </Link>
        <div className="empty-state">
          <Gauge />
          <h1>{tr("Equipo no disponible")}</h1>
        </div>
      </main>
    );
  return (
    <main className="page detail-page">
      <Link className="back-link" to="/market">
        <ArrowLeft /> {tr("Volver al mercado")}
      </Link>
      <div className="detail-grid">
        <div className="detail-visual">
          <span className="product-badge">
            {product.badge && tr(product.badge)}
          </span>
          {product.image ? (
            <img src={product.image} alt={`${tr("Equipo")} ${product.name}`} />
          ) : (
            <span className="detail-image-placeholder">
              <Gauge />
            </span>
          )}
          <div className="visual-caption">
            <Gauge />
            <span>
              <strong>{product.power} TH/s</strong>
              <small>{tr("Potencia de cómputo")}</small>
            </span>
          </div>
        </div>
        <div className="detail-content">
          <div className="detail-contract-head">
            <p className="eyebrow">{tr("Contrato de equipo")}</p>
            <button
              className="icon-action"
              aria-label={tr("Reglas")}
              onClick={() => setContractOpen(true)}
            >
              <Info />
            </button>
          </div>
          <h1>{product.name}</h1>
          <div className="detail-price">
            <small>{tr("Inversión del contrato")}</small>
            <strong>{formatUsd(product.price)}</strong>
          </div>
          <Countdown finishAt={product.finishAt} />
          <div className="detail-stats">
            <span>
              <strong>{formatUsd(product.dailyIncome)}</strong>
              <small>{tr("Ingreso diario estimado")}</small>
            </span>
            <span>
              <strong>{formatUsd(product.totalIncome)}</strong>
              <small>{tr("Ingreso total estimado")}</small>
            </span>
            <span>
              <strong>
                {product.days} {tr("días")}
              </strong>
              <small>{tr("Duración")}</small>
            </span>
          </div>
          <div className="purchase-options">
            <div className="quantity-row">
              <span>
                <small>{tr("Cantidad")}</small>
                <strong>
                  {tr("Máximo")} {max}
                </strong>
              </span>
              <div>
                <button
                  disabled={quantity <= 1}
                  onClick={() => setQuantity((value) => value - 1)}
                  aria-label={tr("Reducir cantidad")}
                >
                  <Minus />
                </button>
                <b>{quantity}</b>
                <button
                  disabled={quantity >= max}
                  onClick={() => setQuantity((value) => value + 1)}
                  aria-label={tr("Aumentar cantidad")}
                >
                  <Plus />
                </button>
              </div>
            </div>
            {Boolean(coupons.data?.length) && (
              <fieldset className="coupon-selector">
                <legend>{tr("Cupón disponible")}</legend>
                {coupons.data?.map((coupon) => (
                  <label
                    className={coupon.id === couponId ? "selected" : ""}
                    key={coupon.id}
                  >
                    <input
                      type="radio"
                      name="coupon"
                      checked={coupon.id === couponId}
                      onChange={() => setCouponId(coupon.id)}
                    />
                    <Ticket />
                    <span>{formatUsd(Number(coupon.coupon_quota))}</span>
                  </label>
                ))}
              </fieldset>
            )}
            <div className="order-total">
              <span>{tr("Total")}</span>
              <strong>{formatUsd(total)}</strong>
              {discount > 0 && <small>-{formatUsd(discount)}</small>}
            </div>
          </div>
          {purchase.isSuccess && (
            <div className="form-feedback success">
              <BadgeCheck /> {tr("Compra completada correctamente.")}
            </div>
          )}
          {purchase.isError && (
            <div className="form-feedback error">
              <CircleAlert /> {purchase.error.message}
            </div>
          )}
          {!reviewing ? (
            <button
              className="button primary full"
              disabled={total > user.balance}
              onClick={() => setReviewing(true)}
            >
              {tr("Continuar con la compra")} <ArrowRight />
            </button>
          ) : (
            <div className="confirm-panel">
              <p className="eyebrow">{tr("Confirmar compra")}</p>
              <div>
                <span>{tr("Equipo")}</span>
                <strong>{product.name}</strong>
              </div>
              <div>
                <span>{tr("Cantidad")}</span>
                <strong>{quantity}</strong>
              </div>
              <div>
                <span>{tr("Total")}</span>
                <strong>{formatUsd(total)}</strong>
              </div>
              <div className="confirm-actions">
                <button
                  className="button secondary"
                  onClick={() => setReviewing(false)}
                >
                  {tr("Cancelar")}
                </button>
                <button
                  className="button primary"
                  disabled={purchase.isPending}
                  onClick={() => purchase.mutate()}
                >
                  {purchase.isPending ? (
                    <LoaderCircle className="spin" />
                  ) : (
                    <ShieldCheck />
                  )}{" "}
                  {tr("Confirmar compra")}
                </button>
              </div>
            </div>
          )}
          {total > user.balance && (
            <div className="form-feedback error">
              <CircleAlert /> {tr("Saldo insuficiente.")}
            </div>
          )}
        </div>
      </div>
      <RemoteNoticeDialog
        open={contractOpen}
        type={8}
        onClose={() => setContractOpen(false)}
      />
    </main>
  );
}
